import { useState } from 'react';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useRole } from '@/hooks/useRole';
import { useTheme } from '@/hooks/useTheme';
import { AccountSecurityDialog } from '@/components/account/AccountSecurityDialog';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { UserAvatar } from '@/components/ui/avatar';
import { ROLE_LABELS } from '@/types/roles';
import { cn } from '@/lib/utils';

interface TopbarProps {
  onMenuClick: () => void;
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const { user, signOut } = useAuth();
  const { role } = useRole();
  const { data: profile } = useProfile(user?.id);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAccountDialog, setShowAccountDialog] = useState(false);

  const themeIcon =
    theme === 'dark' ? (
      <WorklynxIcon name="dark-mode" size={20} />
    ) : theme === 'light' ? (
      <WorklynxIcon name="light-mode" size={20} />
    ) : (
      <WorklynxIcon name="monitor" size={20} />
    );
  const nextTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';

  const handleSignOut = async () => {
    await signOut.mutateAsync();
    navigate('/login');
  };

  const userName = profile?.full_name ?? user?.user_metadata?.full_name ?? user?.email ?? 'User';

  return (
    <header className="relative z-[80] flex h-[4.5rem] shrink-0 items-center justify-between border-b border-slate-200/80 bg-white/90 px-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] backdrop-blur dark:border-slate-700/70 dark:bg-slate-900/90 lg:px-8">
      {/* Left side: hamburger (mobile) */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white lg:hidden"
          aria-label="Toggle menu"
        >
          <WorklynxIcon name="menu" size={20} />
        </button>
        {role && (
          <span className="hidden rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300 sm:block">
            {ROLE_LABELS[role]}
          </span>
        )}
      </div>

      {/* Right side: actions */}
      <div className="flex items-center gap-2.5">
        {/* Theme toggle */}
        <button
          onClick={() => setTheme(nextTheme)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
          aria-label={`Switch to ${nextTheme} theme`}
          title={`Theme: ${theme}`}
        >
          {themeIcon}
        </button>

        <NotificationBell />

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className={cn(
              'flex items-center gap-2 rounded-full border border-transparent p-1.5 pr-2 transition-colors',
              'hover:border-slate-200 hover:bg-white hover:shadow-sm',
              'dark:hover:border-slate-700 dark:hover:bg-slate-800',
            )}
          >
            <UserAvatar
              src={profile?.avatar_url ?? user?.user_metadata?.avatar_url}
              name={userName}
              className="h-8 w-8"
            />
            <span className="hidden text-sm font-medium text-foreground md:block">
              {userName}
            </span>
          </button>

          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-[90] bg-slate-950/20 backdrop-blur-[1px] sm:bg-transparent sm:backdrop-blur-none"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="fixed left-3 right-3 top-[5.25rem] z-[100] max-h-[70dvh] overflow-hidden rounded-2xl border border-slate-200 bg-white py-1.5 shadow-2xl shadow-slate-950/20 dark:border-slate-700 dark:bg-slate-900 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-3 sm:w-64 sm:max-h-none sm:shadow-xl sm:shadow-slate-900/10">
                <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                  <p className="truncate text-sm font-semibold text-slate-950 dark:text-white">
                    {userName}
                  </p>
                  {user?.email && (
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {user.email}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    navigate('/profile');
                    setShowUserMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  <WorklynxIcon name="employee" size={16} />
                  Profile
                </button>
                <button
                  onClick={() => {
                    setShowAccountDialog(true);
                    setShowUserMenu(false);
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  <WorklynxIcon name="settings" size={16} />
                  Account
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-danger transition-colors hover:bg-danger-soft"
                >
                  <WorklynxIcon name="logout" size={16} />
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <AccountSecurityDialog
        open={showAccountDialog}
        onOpenChange={setShowAccountDialog}
        fullName={profile?.full_name ?? user?.user_metadata?.full_name}
        email={user?.email}
      />
    </header>
  );
}
